import { FunctionComponent } from 'react';
import * as vp from 'vopay';
import { useTranslation } from 'react-i18next';
import FileTemplateFormRender from './FileTemplateForm';
import { FileTemplateForm } from './types';
import { useClientData } from '@/providers/ClientData.provider';

type FC = FunctionComponent;

const DownloadTemplate: FC = () => {
  const { t } = useTranslation('translations', { keyPrefix: 'Contacts.DownloadTemplate' });

  const {
    response: { Country },
  } = useClientData();

  const handlers = {
    onDownload: (values: FileTemplateForm) => {
      let fileName = 'eft.csv';

      if (values.Product === 'eft') {
        fileName = 'eft.csv';
      } else if (values.Product === 'e-Transfer') {
        fileName = 'emt.csv';
      } else if (values.Product === 'ACH') {
        fileName = 'ach.csv';
      } else if (values.Product === 'Contact') {
        fileName = 'contact.csv';
      }

      void fetch(`/files/beneficiary/${fileName}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/pdf',
        },
      })
        .then((response) => response.blob())
        .then((blob) => {
          // Create blob link to download
          const url = window.URL.createObjectURL(new Blob([blob]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `${fileName}`);

          // Append to html link element page
          document.body.appendChild(link);

          // Start download
          link.click();

          // Clean up and remove the link
          link.parentNode?.removeChild(link);
        });
    },
  };

  const { Box, Title } = vp;

  return (
    <Box flx="col">
      <Box mb="md" jc="sb">
        <Title title={t('title', { defaultValue: 'File Templates' })} />
      </Box>

      <Box flx="col" jc="sb" mt="md">
        {t('description', {
          defaultValue:
            'In this contacts section you are able to save a payment file of up to 5,000 contacts payment instructions in one file.',
        })}

        <Box flx="col" jc="sb" mt="md">
          <ol style={{ paddingLeft: '40px' }}>
            <li>
              {t('instructions.step1', {
                defaultValue:
                  "Select the type of payment you wish to make and press 'Download'. This will download a .csv file.",
              })}
            </li>
            <li>
              {t('instructions.step2', {
                defaultValue:
                  'Fill out the downloaded .csv file for all the beneficiaries or contacts that you wish to add. For payment templates, all fields must be filled. In the Contact template, the PaymentMethod, PaymentMethodDetails and PaymentMethodCurrency columns are optional. For bank, enter RoutingNumber-AccountNumber (US bank account), InstitutionNumber-TransitNumber-AccountNumber (Canadian bank account) or an IBAN; for email (Interac), paypal, venmo or zelle, enter an email address or phone number. Leave PaymentMethodCurrency blank to use your account currency.',
              })}
            </li>
            <li>{t('instructions.step3', { defaultValue: "Go to the 'File Upload' section and;" })}</li>
            <li>
              {t('instructions.step4', {
                defaultValue: 'Select the type of payment that matches the .csv file you are uploading.',
              })}
            </li>
            <li>
              {t('instructions.step5', {
                defaultValue: "Press the 'Choose File' button and select the file that you wish to upload.",
              })}
            </li>
            <li>{t('instructions.step6', { defaultValue: "Once the file is selected press the 'Submit' button." })}</li>
          </ol>
        </Box>
      </Box>

      <Box flx="col" mt="md">
        <FileTemplateFormRender
          defaultValues={{
            Service: 'Debit',
            Product: Country === 'US' ? 'ACH' : 'EFT',
            Currency: Country === 'US' ? 'USD' : 'CAD',
          }}
          handlers={handlers}
        />
      </Box>
    </Box>
  );
};

export default DownloadTemplate;
